import { useState } from "react";
import {
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Card,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Collapse,
  UnstyledButton,
  Badge,
} from "@mantine/core";
import {
  IconTrash,
  IconPlus,
  IconChevronDown,
  IconChevronUp,
  IconMessage,
  IconPencil,
  IconMessageCircle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import RichTextDiagnosis from "../pathologist-review/RichTextDiagnosis";
import { useAuth } from "../../hooks/useAuth";
import {
  useCaseComments,
  useAddComment,
  useUpdateComment,
  useDeleteComment,
} from "../../hooks/useInternalDiscussions";
import {
  discussionCategoryLabels,
  discussionCategoryColors,
} from "../../services/internalDiscussions.service";
import type { CaseComment } from "../../services/internalDiscussions.service";

interface CaseCommentsSectionProps {
  caseId: string | null;
  /** When true, removes the outer Paper wrapper and the internal heading — for use inside a widget that already provides a title */
  embedded?: boolean;
}

export function CaseCommentsSection({
  caseId,
  embedded,
}: CaseCommentsSectionProps) {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");

  // Fetch all comments for the case (standalone + discussion-linked)
  const { data: comments = [], isLoading } = useCaseComments(caseId || "");

  // Mutations
  const addCommentMutation = useAddComment(
    user ? { id: user.id, fullName: user.fullName } : undefined
  );
  const updateCommentMutation = useUpdateComment();
  const deleteCommentMutation = useDeleteComment();

  const isContentEmpty = (html: string) => !html.replace(/<[^>]*>/g, "").trim();

  const handleAddComment = () => {
    if (!isContentEmpty(newComment) && caseId) {
      addCommentMutation.mutate(
        {
          caseId,
          content: newComment,
          isInternal: true,
        },
        {
          onSuccess: () => {
            setNewComment("");
            setIsAdding(false);
            notifications.show({
              title: "Comment Added",
              message: "Case comment has been saved",
              color: "green",
            });
          },
          onError: (error: any) => {
            notifications.show({
              title: "Error",
              message: error.message || "Failed to add comment",
              color: "red",
            });
          },
        }
      );
    }
  };

  const handleEditComment = (comment: CaseComment) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content);
  };

  const handleSaveEdit = () => {
    if (editingCommentId && !isContentEmpty(editingContent)) {
      updateCommentMutation.mutate(
        {
          commentId: editingCommentId,
          data: { content: editingContent },
        },
        {
          onSuccess: () => {
            setEditingCommentId(null);
            setEditingContent("");
            notifications.show({
              title: "Comment Updated",
              message: "Case comment has been updated",
              color: "green",
            });
          },
          onError: (error: any) => {
            notifications.show({
              title: "Error",
              message: error.message || "Failed to update comment",
              color: "red",
            });
          },
        }
      );
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent("");
  };

  const handleDeleteComment = (commentId: string) => {
    deleteCommentMutation.mutate(commentId, {
      onSuccess: () => {
        notifications.show({
          title: "Comment Deleted",
          message: "Case comment has been removed",
          color: "blue",
        });
      },
      onError: (error: any) => {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to delete comment",
          color: "red",
        });
      },
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const isEdited = (comment: CaseComment) => {
    // Check if updated_at is more than 1 second after created_at
    const created = new Date(comment.createdAt).getTime();
    const updated = new Date(comment.updatedAt).getTime();
    return updated - created > 1000;
  };

  const canModify = (comment: CaseComment) => {
    return comment.userId === user?.id;
  };

  // Only allow collapsing if there are comments
  const canCollapse = comments.length > 0;
  const effectiveExpanded = canCollapse ? isExpanded : true;

  // Get the latest comment for collapsed preview
  const latestComment = comments.length > 0 ? comments[0] : null;

  if (!caseId) {
    if (embedded) {
      return (
        <Text c="dimmed" ta="center" size="sm">
          Save the case first to add comments
        </Text>
      );
    }
    return (
      <Paper p="sm" withBorder>
        <Text c="dimmed" ta="center" size="sm">
          Save the case first to add comments
        </Text>
      </Paper>
    );
  }

  const content = (
    <Stack gap="md">
      {!embedded &&
        (canCollapse ? (
          <UnstyledButton
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ width: "100%" }}
          >
            <Group justify="space-between">
              <Group gap="xs">
                <IconMessage size={16} color="var(--mantine-color-gray-6)" />
                <Text size="sm" fw={500}>
                  Comments
                </Text>
                <Badge size="sm" variant="light" color="teal">
                  {comments.length}
                </Badge>
              </Group>
              {effectiveExpanded ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </Group>
          </UnstyledButton>
        ) : (
          <Group gap="xs">
            <IconMessage size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm" fw={500}>
              Comments
            </Text>
          </Group>
        ))}

      {/* Collapsed preview - show latest comment */}
      {!effectiveExpanded && latestComment && (
        <UnstyledButton
          onClick={() => setIsExpanded(true)}
          style={{ width: "100%" }}
        >
          <Card withBorder p="xs" bg="teal.0">
            <Text size="xs" lineClamp={2} c="dimmed">
              <Text span fw={500} c="dark">
                Latest:
              </Text>{" "}
              {latestComment.content}
            </Text>
          </Card>
        </UnstyledButton>
      )}

      <Collapse in={effectiveExpanded}>
        <Stack gap="md">
          {!isAdding && (
            <Button
              size="xs"
              variant="light"
              color="teal"
              leftSection={<IconPlus size={14} />}
              onClick={() => setIsAdding(true)}
            >
              Add Comment
            </Button>
          )}

          {isAdding && (
            <Stack gap="sm">
              <RichTextDiagnosis
                value={newComment}
                onChange={setNewComment}
                placeholder="Enter comment..."
                minHeight={80}
              />
              <Group justify="flex-end" gap="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    setIsAdding(false);
                    setNewComment("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  color="teal"
                  onClick={handleAddComment}
                  loading={addCommentMutation.isPending}
                  disabled={isContentEmpty(newComment)}
                >
                  Save Comment
                </Button>
              </Group>
            </Stack>
          )}

          {isLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : comments.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center">
              No comments yet
            </Text>
          ) : (
            <Stack gap="xs">
              {comments.map((comment: CaseComment) => (
                <Card key={comment.id} withBorder p="xs" bg="gray.0">
                  {editingCommentId === comment.id ? (
                    // Edit mode
                    <Stack gap="sm">
                      <RichTextDiagnosis
                        value={editingContent}
                        onChange={setEditingContent}
                        minHeight={80}
                      />
                      <Group justify="flex-end" gap="xs">
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="xs"
                          color="teal"
                          onClick={handleSaveEdit}
                          loading={updateCommentMutation.isPending}
                          disabled={isContentEmpty(editingContent)}
                        >
                          Save
                        </Button>
                      </Group>
                    </Stack>
                  ) : (
                    // View mode
                    <Group
                      justify="space-between"
                      align="flex-start"
                      wrap="nowrap"
                    >
                      <Stack gap={4} style={{ flex: 1 }}>
                        {comment.internalDiscussionId && (
                          <Group gap={4}>
                            <Badge
                              size="xs"
                              variant="light"
                              color={
                                comment.internalDiscussion?.category
                                  ? discussionCategoryColors[
                                      comment.internalDiscussion.category
                                    ] || "blue"
                                  : "blue"
                              }
                              leftSection={
                                <IconMessageCircle
                                  size={10}
                                  style={{ marginTop: 1 }}
                                />
                              }
                            >
                              Discussion
                              {comment.internalDiscussion?.category
                                ? ` · ${
                                    discussionCategoryLabels[
                                      comment.internalDiscussion.category
                                    ] || comment.internalDiscussion.category
                                  }`
                                : ""}
                            </Badge>
                          </Group>
                        )}
                        <div
                          style={{
                            wordBreak: "break-word",
                            fontSize: "var(--mantine-font-size-sm)",
                          }}
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: comment.content,
                          }}
                        />
                        <Text size="xs" c="dimmed">
                          {comment.user?.fullName || "Unknown"} •{" "}
                          {formatDate(comment.createdAt)}
                          {isEdited(comment) && (
                            <Text span c="dimmed" fs="italic">
                              {" "}
                              (edited)
                            </Text>
                          )}
                        </Text>
                      </Stack>
                      {canModify(comment) && !comment.internalDiscussionId && (
                        <Group gap={4}>
                          <Tooltip label="Edit comment">
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => handleEditComment(comment)}
                            >
                              <IconPencil size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Delete comment">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="sm"
                              onClick={() => handleDeleteComment(comment.id)}
                              loading={deleteCommentMutation.isPending}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      )}
                    </Group>
                  )}
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );

  if (embedded) return content;
  return (
    <Paper p="sm" withBorder>
      {content}
    </Paper>
  );
}
